import React, { useEffect, useState } from "react"
import { geoCentroid } from "d3-geo"
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Annotation
} from "react-simple-maps"

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json"
const highlight = "#2445a7"

// Offsets copied from the guide component
const offsets = {
  VT: [-15, -35],
  NH: [34, 2],
  MA: [30, -1],
  RI: [28, 2],
  CT: [35, 10],
  NJ: [34, 1],
  DE: [33, 0],
  MD: [47, 10],
  DC: [49, 21],
  FL: [50, 0],
  LA: [0, 45],
  MI: [35, -10]
}

// Gradient colors from the guide
const startColor = "#e33462"
const endColor = "#4e5bc4"

function hexToRgb(hex) {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!match) return null
  const [, r, g, b] = match
  return {
    r: parseInt(r, 16),
    g: parseInt(g, 16),
    b: parseInt(b, 16)
  }
}

function rgbToHex({ r, g, b }) {
  const toHex = (x) => x.toString(16).padStart(2, "0")
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function interpolateColor(c1, c2, factor = 0) {
  const a = hexToRgb(c1)
  const b = hexToRgb(c2)
  if (!a || !b) return c1
  return rgbToHex({
    r: Math.round(a.r + (b.r - a.r) * factor),
    g: Math.round(a.g + (b.g - a.g) * factor),
    b: Math.round(a.b + (b.b - a.b) * factor)
  })
}

const MapChart = ({ states }) => {
  const [activeGeo, setActiveGeo] = useState("")
  const [statesWithColors, setStatesWithColors] = useState([])

  useEffect(() => {
    if (!states || !states.length) {
      setStatesWithColors([])
      return
    }

    // Find the maximum value to normalize counts
    const maxCount = Math.max(...states.map((s) => s.value || 0), 0)

    const withColors = states.map((state) => {
      const raw = maxCount > 0 ? (state.value || 0) / maxCount : 0
      // sqrt to spread out lower values a bit (same trick as guide)
      const factor = Math.sqrt(raw)
      return {
        ...state,
        fill: interpolateColor(startColor, endColor, factor)
      }
    })

    setStatesWithColors(withColors)
  }, [states])

  return (
    <>
      {!!statesWithColors.length && (
        <ComposableMap
          projection="geoAlbersUsa"
          style={{ width: "99%", height: "50rem" }}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) => (
              <>
                {geographies.map((geo) => {
                  const cur = statesWithColors.find((s) => s.id === geo.id)

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => setActiveGeo(geo.id)}
                      onMouseLeave={() => setActiveGeo(null)}
                      style={{
                        default: { outline: "none" },
                        hover: {
                          outline: "none",
                          transition: "all 250ms",
                          fill: highlight,
                          stroke: highlight
                        },
                        pressed: {
                          outline: "none",
                          transition: "all 250ms",
                          fill: highlight,
                          stroke: highlight
                        }
                      }}
                      // Fallback to a light color if for some reason cur is missing
                      fill={
                        geo.id === activeGeo
                          ? highlight
                          : (cur && cur.fill) || "#F5F4F6"
                      }
                      stroke="#FFFFFF"
                    />
                  )
                })}

                {geographies.map((geo) => {
                  const centroid = geoCentroid(geo)
                  const cur = statesWithColors.find((s) => s.id === geo.id)

                  if (!cur) return null

                  const label =
                    cur.value && cur.value > 0
                      ? `${cur.code} (${cur.value})`
                      : cur.code

                  return (
                    <g key={geo.rsmKey + "-name"}>
                      {centroid[0] > -160 &&
                        centroid[0] < -67 &&
                        (Object.keys(offsets).indexOf(cur.code) === -1 ? (
                          <Marker coordinates={centroid}>
                            <text
                              y="2"
                              fontSize={14}
                              textAnchor="middle"
                              style={{ cursor: "pointer" }}
                              fill={geo.id === activeGeo ? "#FFFFFF" : "#000000"}
                              onMouseEnter={() => setActiveGeo(geo.id)}
                              onMouseLeave={() => setActiveGeo(null)}
                            >
                              {label}
                            </text>
                          </Marker>
                        ) : (
                          <Annotation
                            subject={centroid}
                            dx={offsets[cur.code][0]}
                            dy={offsets[cur.code][1]}
                          >
                            <text
                              x={4}
                              fontSize={14}
                              alignmentBaseline="middle"
                              style={{ cursor: "pointer" }}
                              onMouseEnter={() => setActiveGeo(geo.id)}
                              onMouseLeave={() => setActiveGeo(null)}
                            >
                              {label}
                            </text>
                          </Annotation>
                        ))}
                    </g>
                  )
                })}
              </>
            )}
          </Geographies>
        </ComposableMap>
      )}
    </>
  )
}

export default MapChart
